import { randomPositionInSphere } from "@/helpers/randomPositionInSphere";
import type { ParticleSystem } from "@/types";
import { Vector3 } from "three";
import { linearInterpolation } from "./linearInterpolation";
import { randomColorInRange } from "./randomColorInRange";

/**
 * This generates all particles upfront (instead of generating them on the fly).
 * All particles are emitted within a sphere.
 */
export const generateSphereEmitter = (config: ParticleSystem) => {
  const {
    color1,
    color2,
    capacity,
    emitRate,
    emitRateGradients,
    particleEmitterType,
    minLifeTime,
    maxLifeTime,
    minEmitPower,
    maxEmitPower,
    minAngularSpeed,
    maxAngularSpeed,
    minSize,
    maxSize,
    minInitialRotation,
    maxInitialRotation,
  } = config;
  const { radius, radiusRange, directionRandomizer } = particleEmitterType;

  // Shaders expect data as flat arrays. For example, an RGBA color will use 4
  // array slots whereas a 3D position will use 3 array slots.
  const colors = new Float32Array(capacity * 4);
  const positions = new Float32Array(capacity * 3);
  const directions = new Float32Array(capacity * 3);
  const emitTimes = new Float32Array(capacity);
  const lifetimes = new Float32Array(capacity);
  const sizes = new Float32Array(capacity);
  const angularSpeeds = new Float32Array(capacity);
  const zRotations = new Float32Array(capacity);

  const radialDirection = new Vector3();
  const randomDirection = new Vector3();
  const direction = new Vector3();

  // Capacity is the total number of particles to generate.
  // TODO: we could update this to loop back around if we want a perpetual emitter.
  for (let i = 0; i < capacity; i++) {
    const i3 = i * 3;
    const i4 = i * 4;

    // Generate an initial color between color1 and color2
    const [r, g, b, a] = randomColorInRange(color1, color2);
    colors[i4] = r;
    colors[i4 + 1] = g;
    colors[i4 + 2] = b;
    colors[i4 + 3] = a;

    // Generate a random position in the sphere
    const [x, y, z] = randomPositionInSphere(radius, radiusRange);
    positions[i3] = x;
    positions[i3 + 1] = y;
    positions[i3 + 2] = z;

    // Generate movement direction. Calculate the radial direction and a random
    // direction, and lerp between the two. directionRandomizer of 0 means only
    // travel in the radial direction, 1 means travel in the random direction.
    radialDirection.set(x, y, z).normalize(); // assumes origin is (0,0,0)
    randomDirection.randomDirection(); // already normalized

    const speed = Math.random() * (maxEmitPower - minEmitPower) + minEmitPower;
    direction
      .copy(radialDirection)
      .lerp(randomDirection, directionRandomizer)
      .normalize()
      .multiplyScalar(speed);

    directions[i3] = direction.x;
    directions[i3 + 1] = direction.y;
    directions[i3 + 2] = direction.z;

    // If we are using an emit rate gradient, we will set emit times later.
    if (!(emitRate || emitRateGradients?.length)) {
      emitTimes[i] = 0;
    } else if (emitRate > 0) {
      emitTimes[i] = i / emitRate;
    }

    // Random starting time offset (min lifetime to max lifetime)
    lifetimes[i] = Math.random() * (maxLifeTime - minLifeTime) + minLifeTime;

    // Random size for each particle
    // TODO: why do I need to multiply by some scale here? otherwise the particles are tiny
    sizes[i] = (Math.random() * (maxSize - minSize) + minSize) * 2;

    // Random angular speed
    angularSpeeds[i] =
      Math.random() * (maxAngularSpeed - minAngularSpeed) + minAngularSpeed;

    // Random z-rotation for each particle
    zRotations[i] =
      Math.random() * (maxInitialRotation - minInitialRotation) +
      minInitialRotation;
  }

  // Sometimes the particle system will use an emit rate gradient instead of a fixed rate.
  if (!emitRate && emitRateGradients?.length > 0) {
    let particle = 0;
    const duration = 2;
    const delta = 0.015;
    let accumulatedParticles = 0; // Track fractional particles
    for (let time = 0; particle < capacity; time += delta) {
      const finalRate = emitRateGradients[emitRateGradients.length - 1].factor1;
      const rate = Math.min(
        linearInterpolation(time, emitRateGradients),
        finalRate,
      );

      // Accumulate fractional particles
      accumulatedParticles += rate * delta;

      // Emit whole particles
      const numberOfParticlesInChunk = Math.floor(accumulatedParticles);
      accumulatedParticles -= numberOfParticlesInChunk; // Keep the fractional remainder

      for (
        let chunkedParticle = 0;
        chunkedParticle < numberOfParticlesInChunk && particle < capacity;
        chunkedParticle++, particle++
      ) {
        emitTimes[particle] =
          (time + chunkedParticle * (delta / numberOfParticlesInChunk)) *
          duration;
      }
    }
  }

  return {
    positions,
    directions,
    emitTimes,
    lifetimes,
    sizes,
    colors,
    angularSpeeds,
    zRotations,
  };
};
